import { clearActingOrganizationPublicId } from "../helpers/actingOrganization";
import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    InteractionStatus,
} from "@azure/msal-browser";

export const Roles = {
    Admin: "Admin",
    CustomerService: "CustomerService",
    Partner: "Partner",
    Customer: "Customer", 
    ServicePartner: "ServicePartner",
    CpmsLight: "CpmsLight",
};

export const RoleLabelsForExternal = {
    CustomerService: "Customer service",
    Partner: "Partner",
    Customer: "Customer",
    ServicePartner: "Service partner",
    CpmsLight: "Cpms light",
};

export const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_CLIENT_ID,
        authority: process.env.REACT_APP_AUTHORITY,
        redirectUri: process.env.REACT_APP_REDIRECT_URI,
    },
    cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: true,
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);


const AuthContext = createContext(null);

let tokenPromise = null;

export const AuthProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rolesLoading, setRolesLoading] = useState(true);

    const isAuthenticated = accounts.length > 0;
    const DASHBOARD_FILTER_COOKIE = "statusDashboardFilters_v1";

    function deleteCookie(name) {
        document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/; SameSite=Lax`;
    }

    useEffect(() => {
        let cancelled = false;

        const syncAuth = async () => {
            if (inProgress !== InteractionStatus.None) return;

            if (!accounts.length) {
                if (!cancelled) {
                    setUser(null);
                    setRolesLoading(false);
                    setLoading(false);
                }
                return;
            }

            try {
                const account = accounts[0];
                instance.setActiveAccount(account);

                setRolesLoading(true);

                const response = await instance.acquireTokenSilent({
                    scopes: process.env.REACT_APP_API_SCOPE.split(","),
                    account,
                });

                const claims = decodeJwt(response.accessToken);
                const roles = claims.roles || [];
                if (!roles.includes(Roles.Admin)) {
                    clearActingOrganizationPublicId();
                }

                if (!cancelled) {
                    setUser({
                        name: account.name,
                        username: account.username,
                        homeAccountId: account.homeAccountId,
                        localAccountId: account.localAccountId,
                        environment: account.environment,
                        roles,
                    });

                    setRolesLoading(false);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Auth sync failed:", err);
                if (!cancelled) {
                    setUser(null);
                    setRolesLoading(false);
                    setLoading(false);
                }
            }
        };

        syncAuth();
        return () => {
            cancelled = true;
        };
    }, [accounts, inProgress, instance]);

    useEffect(() => {
        const callbackId = instance.addEventCallback((message) => {
            if (message.eventType === "LOGIN_SUCCESS") {
                const redirectPath =
                    sessionStorage.getItem("redirectPath") || "/";
                sessionStorage.removeItem("redirectPath");
                navigate(redirectPath);
            }

            if (message.eventType === "LOGOUT_SUCCESS") {
                clearActingOrganizationPublicId();
                setUser(null);
            }
        });

        return () => {
            if (callbackId) instance.removeEventCallback(callbackId);
        };
    }, [instance, navigate]);

    const login = () => {
        if (inProgress === InteractionStatus.None) {
            instance.loginRedirect();
        }
    };

    const logout = () => {
        if (inProgress === InteractionStatus.None) {
            clearActingOrganizationPublicId();
            deleteCookie(DASHBOARD_FILTER_COOKIE);
            instance.logoutRedirect();
        }
    };

    const getAccessToken = async () => {
        if (!isAuthenticated) {
            throw new Error("User is not authenticated");
        }

        const account = instance.getActiveAccount() || accounts[0];
        if (!account) {
            throw new Error("No active account");
        }

        if (!tokenPromise) {
            tokenPromise = instance
                .acquireTokenSilent({
                    scopes: process.env.REACT_APP_API_SCOPE.split(","),
                    account,
                })
                .then((res) => {
                    tokenPromise = null;
                    return res.accessToken;
                })
                .catch((err) => {
                    tokenPromise = null;

                    if (err instanceof InteractionRequiredAuthError) {
                        if (inProgress === InteractionStatus.None) {
                            instance.acquireTokenRedirect({
                                scopes: process.env.REACT_APP_API_SCOPE.split(","),
                                account,
                            });
                        }
                    }

                    throw err;
                });
        }

        return tokenPromise;
    };

    const hasRole = (r) => user?.roles?.includes(r) || false;
    const hasAnyRole = (wanted) =>
        wanted.some((r) => user?.roles?.includes(r));
    const roles = () => user?.roles || [];

    const value = useMemo(
        () => ({
            isAuthenticated,
            user,
            login,
            logout,
            getAccessToken,
            loading,
            rolesLoading,
            hasRole,
            hasAnyRole,
            roles,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
        [isAuthenticated, user, loading, rolesLoading]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

function decodeJwt(jwt) {
    const [, payload] = jwt.split(".");
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
        atob(b64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
    );
    return JSON.parse(json);
}
